import { useTranslation } from 'react-i18next';
import './Services.css';

const RTL_LANGUAGES = ['ar', 'fa', 'he', 'ur'];

const getDirection = (language = 'en') => {
  const normalizedLanguage = String(language).toLowerCase();

  if (
    RTL_LANGUAGES.some(
      (languageCode) =>
        normalizedLanguage === languageCode ||
        normalizedLanguage.startsWith(`${languageCode}-`) ||
        normalizedLanguage.startsWith(`${languageCode}_`)
    ) ||
    normalizedLanguage.includes('arabic') ||
    normalizedLanguage.includes('farsi') ||
    normalizedLanguage.includes('persian') ||
    normalizedLanguage.includes('hebrew') ||
    normalizedLanguage.includes('urdu')
  ) {
    return 'rtl';
  }

  return 'ltr';
};

const ServicePlansPage = () => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n?.resolvedLanguage || i18n?.language || 'en';
  const direction = getDirection(currentLanguage);

  const platformPlans = [
    {
      id: 'starter',
      name: t('Starter'),
      price: t('$500 / month / token'),
      description: t(
        'Basic SaaS management service for each active token. Suitable for a small number of assets or an initial launch.'
      ),
      features: [
        { id: 'basic-workflow-access', label: t('Basic asset and token workflow access') },
        { id: 'simple-listing-support', label: t('Simple listing and platform service support') },
        { id: 'small-launch', label: t('Good starting point for one asset or a small launch') }
      ]
    },
    {
      id: 'professional',
      name: t('Professional'),
      price: t('$3,500 / month / 10 tokens'),
      description: t(
        'Portfolio-level SaaS management service for a minimum of ten active tokens with a lower effective cost per token.'
      ),
      features: [
        { id: 'growing-portfolios', label: t('Designed for growing asset portfolios') },
        { id: 'expanded-reporting-support', label: t('Expanded reporting and workflow support') },
        { id: 'lower-effective-cost', label: t('Lower effective cost per token for multiple active tokens') }
      ]
    },
    {
      id: 'enterprise-custom',
      name: t('Enterprise / Custom'),
      price: t('From $5,000 / month'),
      description: t(
        'Custom deployment and support for enterprise customers, larger portfolios, custom workflows, and advanced operational needs.'
      ),
      features: [
        { id: 'custom-service-setup', label: t('Custom service setup and enterprise support') },
        {
          id: 'advanced-operational-support',
          label: t('Advanced data, listing, reporting, and operational workflow support')
        },
        {
          id: 'separate-agreement',
          label: t(
            'Sales responsibility, platform display, data support, listing support, and allowed transaction fees must be defined in a separate agreement'
          )
        }
      ]
    }
  ];

  const serviceRoles = [
    { id: 'asset-owner', label: t('Asset owner') },
    { id: 'sponsor', label: t('Sponsor') },
    { id: 'issuer', label: t('Issuer') },
    { id: 'operator', label: t('Operator') }
  ];

  const transactionFeeFeatures = [
    {
      id: 'completed-transaction',
      label: t('Charged only after a completed purchase or sale')
    },
    {
      id: 'approved-transaction-users',
      label: t(
        'Charged only to an approved Broker-dealer signer, transfer agent, or token operations user'
      )
    },
    {
      id: 'not-monthly-plan-users',
      label: t(
        'Not charged to public visitors, asset owners, sponsors, issuers, or operators under the monthly service plans'
      )
    },
    {
      id: 'agreement-and-legal-requirements',
      label: t('Handled under the applicable agreement, approval process, and legal requirements')
    }
  ];

  return (
    <main
      className={`services-page ${direction === 'rtl' ? 'services-page-rtl' : 'services-page-ltr'}`}
      dir={direction}
      lang={currentLanguage}
    >
      <section className="services-hero" aria-labelledby="services-page-title">
        <p className="services-eyebrow">{t('Stark RWAE Exchange')}</p>
        <h1 id="services-page-title">{t('Services')}</h1>
        <p className="services-hero-text">
          {t(
            'Simple service options for asset owners, sponsors, issuers, operators, and approved transaction service users.'
          )}
        </p>
      </section>

      <section className="services-section" aria-labelledby="platform-service-plan-title">
        <div className="services-section-header">
          <p className="services-eyebrow">{t('Platform service plans')}</p>
          <h2 id="platform-service-plan-title">
            {t('Asset owner, sponsor, issuer, or operator service plan')}
          </h2>
          <p>
            {t(
              'These monthly service plans are for project-side users who need platform services for assets, tokens, listings, data, reporting, and related workflows.'
            )}
          </p>
        </div>

        <div className="services-role-list" aria-label={t('Included roles')}>
          {serviceRoles.map((role) => (
            <span className="services-role-pill" key={role.id}>
              {role.label}
            </span>
          ))}
        </div>

        <div className="plans-container">
          {platformPlans.map((plan) => (
            <article className="plan-card" key={plan.id}>
              <div className="plan-card-header">
                <h3>{plan.name}</h3>
                <div className="plan-price">{plan.price}</div>
              </div>

              <p className="plan-description">{plan.description}</p>

              <ul className="plan-features">
                {plan.features.map((feature) => (
                  <li key={feature.id}>{feature.label}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="services-section services-fee-section" aria-labelledby="transaction-service-fee-title">
        <div className="services-section-header">
          <p className="services-eyebrow">{t('Transaction service fee')}</p>
          <h2 id="transaction-service-fee-title">
            {t('Broker-dealer signer, transfer agent, or token operations user')}
          </h2>
          <p>
            {t(
              'This service fee applies only to approved transaction, transfer, or token operations service users when a purchase or sale is completed.'
            )}
          </p>
        </div>

        <article className="transaction-fee-card">
          <div className="transaction-fee-main">
            <span className="transaction-fee-rate">{t('0.5%')}</span>
            <span className="transaction-fee-label">{t('after completed buy or sell transaction')}</span>
          </div>

          <ul className="plan-features transaction-fee-list">
            {transactionFeeFeatures.map((feature) => (
              <li key={feature.id}>{feature.label}</li>
            ))}
          </ul>
        </article>
      </section>
    </main>
  );
};

export default ServicePlansPage;
